import { useMutation, useQuery } from '@apollo/client';
import SearchIcon from '@mui/icons-material/Search';
import { Button, RadioGroup } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Radio from '@mui/material/Radio';
import TextField from '@mui/material/TextField';
import { ConfirmDialog } from 'components/common/confirm-dialog';
import { SearchableTableLayout } from 'components/common/layout/searchable-table';
import { Table } from 'components/common/table';
import { useNavTableConnection } from 'hooks/use-nav-table-connection';
import { useQueryParamState } from 'hooks/use-query-params';
import { useSnackbar } from 'providers/snackbar';
import { FC, useState } from 'react';
import { useParams } from 'react-router';
import {
  CampaignAgentPhones as ICampaignAgentPhones,
  CampaignAgentPhonesVariables,
} from 'types/apollo/CampaignAgentPhones';
import { DrainAgentPhone, DrainAgentPhoneVariables } from 'types/apollo/DrainAgentPhone';
import { AgentPhoneStatus } from 'types/apollo/globalTypes';
import { BuyPhoneDialog } from './buy-phone-dialog';
import { CampaignAgentPhonesQuery, DrainAgentPhoneMutation } from './campaign-agent-phones.graphql';
import { applyFilter, canDrain, countLivePhones, FilterStatus } from './filter';

export interface Data {
  id: string;
  phone: string;
  messagingServiceId: string | null;
  locality: string;
  region: string;
  status: AgentPhoneStatus;
  vendorId: number;
}

const statusToDisplayStringMap = new Map<FilterStatus, string>([
  ['ALL', 'All'],
  [AgentPhoneStatus.ACTIVE, 'Active'],
  [AgentPhoneStatus.INACTIVE, 'Inactive'],
  [AgentPhoneStatus.DRAINING, 'Draining'],
]);

export const CampaignAgentPhones: FC = () => {
  const { campaignId: id } = useParams();
  const [search, setSearch] = useQueryParamState('search', '');
  const [statusFilter, setStatusFilter] = useQueryParamState('status', 'ALL');

  const [buyOpen, setBuyOpen] = useState(false);
  // Split from the open flag so the number stays rendered through the exit transition.
  const [drainTarget, setDrainTarget] = useState('');
  const [drainOpen, setDrainOpen] = useState(false);
  const { openSnackbar } = useSnackbar();

  const [drainPhone, { loading: drainInFlight }] = useMutation<
    DrainAgentPhone,
    DrainAgentPhoneVariables
  >(DrainAgentPhoneMutation);

  const { data, loading, refetch } = useQuery<ICampaignAgentPhones, CampaignAgentPhonesVariables>(
    CampaignAgentPhonesQuery,
    {
      variables: { campaignId: id ?? '0' },
    },
  );

  const agentPhones = data?.campaign?.agentPhones ?? [];

  const tableData = useNavTableConnection<Data>([
    {
      id: 'phone',
      label: 'Phone',
      numeric: false,
    },
    {
      id: 'messagingServiceId',
      label: 'Messaging Service',
      numeric: false,
    },
    {
      id: 'locality',
      label: 'Locality',
      numeric: false,
    },
    {
      id: 'region',
      label: 'Region',
      numeric: false,
    },
    {
      id: 'status',
      label: 'Status',
      numeric: false,
      renderer: (row) => statusToDisplayStringMap.get(row.status),
    },
    {
      id: 'id',
      label: '',
      numeric: false,
      renderer: (row) =>
        canDrain(row) ? (
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              setDrainTarget(row.phone);
              setDrainOpen(true);
            }}
          >
            Drain
          </Button>
        ) : null,
    },
  ]);

  const refresh = () =>
    refetch().catch(() =>
      openSnackbar({ message: 'Reload to see the current numbers', severity: 'error' }),
    );

  const confirmDrain = async () => {
    if (!drainTarget || !id) {
      return;
    }
    try {
      await drainPhone({ variables: { input: { campaignId: id, phoneNumber: drainTarget } } });
      setDrainOpen(false);
      openSnackbar({ message: `${drainTarget} is draining`, severity: 'success' });
      refresh();
    } catch (err) {
      setDrainOpen(false);
      const reason = err instanceof Error ? err.message : '';
      openSnackbar({
        message: reason
          ? `Could not drain ${drainTarget}: ${reason}`
          : `Could not drain ${drainTarget}`,
        severity: 'error',
      });
    }
  };

  const handleStatusFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStatusFilter((event.target as HTMLInputElement).value as FilterStatus);
  };

  const tableRows = applyFilter({ search, status: statusFilter }, agentPhones);

  return (
    <SearchableTableLayout>
      <SearchableTableLayout.FiltersWrapper>
        <Button variant="contained" onClick={() => setBuyOpen(true)} disabled={!id}>
          Buy another number
        </Button>
        <TextField
          name="search"
          label="Search by Name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl>
          <FormLabel>Status</FormLabel>
          <RadioGroup
            aria-label="Status"
            name="statusFilter"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            sx={{ flexDirection: { xs: 'row', lg: 'column' } }}
          >
            <FormControlLabel
              value="ALL"
              control={<Radio />}
              label={statusToDisplayStringMap.get('ALL') || ''}
            />
            <FormControlLabel
              value={AgentPhoneStatus.ACTIVE}
              control={<Radio />}
              label={statusToDisplayStringMap.get(AgentPhoneStatus.ACTIVE) || ''}
            />
            <FormControlLabel
              value={AgentPhoneStatus.INACTIVE}
              control={<Radio />}
              label={statusToDisplayStringMap.get(AgentPhoneStatus.INACTIVE) || ''}
            />
            <FormControlLabel
              value={AgentPhoneStatus.DRAINING}
              control={<Radio />}
              label={statusToDisplayStringMap.get(AgentPhoneStatus.DRAINING) || ''}
            />
          </RadioGroup>
        </FormControl>
      </SearchableTableLayout.FiltersWrapper>
      <SearchableTableLayout.TableWrapper>
        <Table loading={loading} rows={tableRows} {...tableData} />
      </SearchableTableLayout.TableWrapper>
      <ConfirmDialog
        open={drainOpen}
        title="Drain this number?"
        message={
          <>
            <strong>{drainTarget}</strong> stops taking new traffic. It stays on the campaign as
            Draining and is not released — selling it back is a separate step. This cannot be undone
            from here.
          </>
        }
        confirmLabel="Drain"
        loading={drainInFlight}
        onConfirm={confirmDrain}
        onCancel={() => setDrainOpen(false)}
      />
      <BuyPhoneDialog
        open={buyOpen}
        campaignId={id ?? ''}
        liveCount={countLivePhones(agentPhones)}
        onClose={() => setBuyOpen(false)}
        onPurchased={refresh}
      />
    </SearchableTableLayout>
  );
};
